import type { StatusTone } from '@portals-platform/ui';

/**
 * The certificate portal's status taxonomy and its mapping onto the design system's
 * semantic tones. This lives in the product, not in `packages/ui` — the shared library
 * has no business knowing what "REFERRED" or "PENDING_RENEWAL" mean. Keys are the API's own
 * upper-case values, so they pass to and from the backend unchanged.
 */
export type CertStatus =
  | 'ACTIVE'
  | 'LIVE'
  | 'DRAFT'
  | 'PENDING_TRANSLATION'
  | 'PENDING_POLICY_NUMBER'
  | 'PENDING_RENEWAL'
  | 'REFERRED'
  | 'RENEWED'
  | 'READY'
  | 'ISSUED'
  | 'HAS_CHANGES'
  | 'CANCELLED'
  | 'QUOTED'
  | 'PENDING'
  | 'EXPIRED'
  | 'DELETED';

export const CERT_STATUS: Record<CertStatus, { label: string; tone: StatusTone }> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  LIVE: { label: 'Live', tone: 'success' },
  DRAFT: { label: 'Draft', tone: 'neutral' },
  PENDING_TRANSLATION: { label: 'Pending Translation', tone: 'warning' },
  PENDING_POLICY_NUMBER: { label: 'Pending Policy Number', tone: 'warning' },
  PENDING_RENEWAL: { label: 'Pending Renewal', tone: 'info' },
  REFERRED: { label: 'Referred', tone: 'warning' },
  RENEWED: { label: 'Renewed', tone: 'success' },
  READY: { label: 'Ready', tone: 'info' },
  ISSUED: { label: 'Issued', tone: 'success' },
  HAS_CHANGES: { label: 'Has Changes', tone: 'warning' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  QUOTED: { label: 'Quoted', tone: 'info' },
  PENDING: { label: 'Pending', tone: 'warning' },
  EXPIRED: { label: 'Expired', tone: 'danger' },
  DELETED: { label: 'Deleted', tone: 'neutral' },
};

/** The only values `GET /certificates/search` accepts for `status`. */
export const CERT_SEARCH_STATUSES: CertStatus[] = [
  'LIVE',
  'PENDING_TRANSLATION',
  'REFERRED',
  'PENDING_RENEWAL',
  'DRAFT',
  'EXPIRED',
  'PENDING_POLICY_NUMBER',
];

export const isReferred = (status: string): boolean => status === 'REFERRED';

/** The label and tone for an API status; an unmapped one shows its raw value, neutrally. */
export const describeCertStatus = (status: string): { label: string; tone: StatusTone } =>
  CERT_STATUS[status as CertStatus] ?? { label: status, tone: 'neutral' };
